import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks";
import { useGetUserProfile } from "../services/api/user.api";
import { useErrorResponse } from "../hooks/useErrorResponse";
import { AppUserSession } from "../services/user-session";
import { User } from "../types/user";
import ScreenLoader from "../components/Loader/ScreenLoader";
import { validateToken } from "../utils";
import { useGetBusinessProfile } from "../services/api/business/business.api";
import usebusinessInfo from "../hooks/useBusiness";

// Type definition for WrappedComponent's props
interface WithOutAuthenticationProps {
  children?: React.ReactNode;
}

const withOutAuthentication = (
  WrappedComponent: React.ComponentType<WithOutAuthenticationProps>
) => {
  return (props: WithOutAuthenticationProps) => {
    const { userInfo, setUser } = useAuth();
    const accessToken = AppUserSession.getToken();
    const [userLoading, setUserLoading] = React.useState(false);
    const [shouldRender, setShouldRender] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const { businessInfo, setBusinessInfo } = usebusinessInfo();
    const [businessLoading, setBusinessLoading] = React.useState(false);

    const {
      refetch,
      data: user,
      error,
    } = useGetUserProfile({
      key: ["user-profile"],
      options: { enabled: false },
    });

    const {
      refetch: businessRefetch,
      data: businessProfile,
      error: businessError,
    } = useGetBusinessProfile({
      key: ["business-profile"],
      options: { enabled: false },
    });

    if (accessToken && !userInfo && !userLoading) {
      const isTokenValid = validateToken(accessToken);
      if (!isTokenValid) {
        localStorage.clear();
      } else {
        setUserLoading(true);
        refetch();
      }
    }

    React.useEffect(() => {
      if (user?.data) {
        setUser(user?.data as User);
        setUserLoading(false);
      }
    }, [user, setUser]);

    useErrorResponse(error);

    React.useEffect(() => {
      if (user?.data?.account_type === "Business" && !businessProfile) {
        setBusinessLoading(true);
        businessRefetch();
      }
    }, [user?.data?.account_type, businessProfile, businessRefetch]);

    React.useEffect(() => {
      if (businessProfile?.data) {
        setBusinessInfo(businessProfile.data);
        setBusinessLoading(false);
      }
    }, [businessProfile]);

    useErrorResponse(businessError);

    // const isLoggedIn = !!accessToken && (!!userInfo || !!businessInfo);

    const isLoggedIn = !!accessToken && !!userInfo;

    // Handle redirects and determine if we should render
    React.useEffect(() => {
      if (userLoading) return;

      if (isLoggedIn && userInfo) {
        // Check for stored fanCard redirect URL first
        const storedRedirectUrl = localStorage.getItem("fanCardRedirectUrl");

        if (storedRedirectUrl) {
          console.log(
            "withOutAuthentication: Redirecting to stored fanCard URL:",
            storedRedirectUrl
          );
          localStorage.removeItem("fanCardRedirectUrl");
          // Use a small delay to ensure the user is fully authenticated
          setTimeout(() => {
            window.location.href = storedRedirectUrl;
          }, 100);
          setShouldRender(false);
          return;
        }

        // Check for stored shared booking redirect URL
        const sharedBookingRedirect = localStorage.getItem(
          "sharedBookingRedirect"
        );
        if (sharedBookingRedirect) {
          console.log(
            "withOutAuthentication: Redirecting to stored shared booking URL:",
            sharedBookingRedirect
          );
          localStorage.removeItem("sharedBookingRedirect");
          // Use a small delay to ensure the user is fully authenticated
          setTimeout(() => {
            window.location.href = sharedBookingRedirect;
          }, 100);
          setShouldRender(false);
          return;
        }

        // Default redirect logic for logged-in users
        if (userInfo.account_type === "Talent") {
          navigate(`/user/${userInfo.id}`);
        } else if (userInfo.account_type === "User") {
          navigate(`/TNPage`);
        } else if (userInfo.account_type === "Business") {
          navigate(`/TNPage`);
        } else {
          navigate(`/TNPage`);
        }
        setShouldRender(false);
      } else {
        // For non-logged-in users, check if they need to be redirected to sign-in
        if (location.pathname.includes("interaction/")) {
          // Store the current URL in localStorage before redirecting to sign-in
          const currentUrl = window.location.href;
          const existingUrl = localStorage.getItem("fanCardRedirectUrl");

          // Only store if not already stored, if it's different, and if it's not a sign-in URL
          if (
            (!existingUrl || existingUrl !== currentUrl) &&
            !currentUrl.includes("/sign-in")
          ) {
            console.log(
              "withOutAuthentication: Redirecting interaction to sign-in, storing URL:",
              currentUrl
            );
            localStorage.setItem("fanCardRedirectUrl", currentUrl);
          } else {
            console.log(
              "withOutAuthentication: URL already stored or is sign-in URL, skipping:",
              currentUrl
            );
          }

          navigate("/sign-in", {
            state: {
              redirect: location.pathname + location.search,
            },
          });
          setShouldRender(false);
        } else if (location.pathname.includes("my-deck")) {
          // Store the current URL in localStorage before redirecting to sign-in
          const currentUrl = window.location.href;
          const existingUrl = localStorage.getItem("fanCardRedirectUrl");

          // Only store if not already stored, if it's different, and if it's not a sign-in URL
          if (
            (!existingUrl || existingUrl !== currentUrl) &&
            !currentUrl.includes("/sign-in")
          ) {
            console.log(
              "withOutAuthentication: Redirecting my-deck to sign-in, storing URL:",
              currentUrl
            );
            localStorage.setItem("fanCardRedirectUrl", currentUrl);
            console.log(
              "withOutAuthentication: Stored URL in localStorage:",
              localStorage.getItem("fanCardRedirectUrl")
            );
          } else {
            console.log(
              "withOutAuthentication: URL already stored or is sign-in URL, skipping:",
              currentUrl
            );
          }

          navigate("/sign-in", {
            state: {
              redirect: location.pathname + location.search,
            },
          });
          setShouldRender(false);
        } else if (
          location.pathname.includes("/sessions") ||
          location.pathname.includes("/user/session/") ||
          location.pathname.includes("/user/group-session/") ||
          location.pathname.includes("/user/spade-session/") ||
          location.pathname.includes("/user/gtp-session/")
        ) {
          // Store booking URLs in localStorage before redirecting to sign-in
          const currentUrl = window.location.href;
          const searchParams = new URLSearchParams(location.search);
          const bookingParams = [
            "bookingId",
            "cardId",
            "shared",
            "tab",
            "channelName",
            "date",
            "timeRange",
            "chip",
            "talentName",
            "cardName",
          ];

          const hasBookingParams = bookingParams.some((param) =>
            searchParams.has(param)
          );

          // If it's a booking-related URL (has params or is a session route)
          if (
            hasBookingParams ||
            location.pathname.includes("/sessions") ||
            location.pathname.includes("/user/")
          ) {
            const existingUrl = localStorage.getItem("sharedBookingRedirect");

            // Only store if not already stored, if it's different, and if it's not a sign-in URL
            if (
              (!existingUrl || existingUrl !== currentUrl) &&
              !currentUrl.includes("/sign-in")
            ) {
              console.log(
                "withOutAuthentication: Redirecting booking URL to sign-in, storing URL:",
                currentUrl
              );
              localStorage.setItem("sharedBookingRedirect", currentUrl);
            } else {
              console.log(
                "withOutAuthentication: URL already stored or is sign-in URL, skipping:",
                currentUrl
              );
            }

            navigate("/sign-in", {
              state: {
                redirect: location.pathname + location.search,
              },
            });
            setShouldRender(false);
          } else {
            setShouldRender(true);
          }
        } else {
          setShouldRender(true);
        }
      }
    }, [isLoggedIn, userInfo, userLoading, navigate, location]);

    // Show loader while determining authentication state
    if (userLoading || !shouldRender) {
      return <ScreenLoader />;
    }

    return <WrappedComponent {...props} />;
  };
};

export default withOutAuthentication;
